import { useState, useEffect, useRef } from 'react';

export default function FavoritesDropdown({ favorites = [], searchQuery = '', onSelect = () => {}, onClose = () => {}, expanded = true, containerRef: externalRef = null }) {
  const q = (searchQuery || '').toLowerCase();
  // Filter then sort alphanumerically by display name (fallback to channel name)
  const filtered = favorites
    .filter((f) => {
      const name = (f.channel_display_name || f.channelDisplayName || f.channel_name || f.channelName || '').toLowerCase();
      const slug = (f.channel_name || f.channelName || '').toLowerCase();
      return name.includes(q) || slug.includes(q);
    })
    .slice()
    .sort((a, b) => {
      const an = (a.channel_display_name || a.channelDisplayName || a.channel_name || a.channelName || '').toLowerCase();
      const bn = (b.channel_display_name || b.channelDisplayName || b.channel_name || b.channelName || '').toLowerCase();
      return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' });
    });

  if (!favorites || favorites.length <= 1) return null;

  const [active, setActive] = useState(0);
  const innerRef = useRef(null);
  const containerRef = externalRef || innerRef;

  useEffect(() => {
    // reset active when filtered list changes
    setActive(0);
  }, [q, favorites.length]);

  // Note: do not auto-focus when opened via typing to avoid stealing input focus.
  // Parent should explicitly focus the dropdown when needed (e.g. on ArrowDown).

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const f = filtered[active];
      if (f) {
        /* c8 ignore start */
        const slug = (f.channel_name || f.channelName || '').toLowerCase();
        /* c8 ignore stop */
        onSelect(slug);
      }
    /* c8 ignore start */
    } else if (e.key === 'Escape') {
    /* c8 ignore stop */
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={`absolute left-0 top-full z-50 mt-1 w-full bg-dark-800 border border-dark-600 rounded-md shadow-lg max-h-56 overflow-auto ${expanded ? '' : 'hidden'}`}
      role="listbox"
      aria-label="Favorite channels"
    >
      {filtered.map((f, i) => {
        const name = f.channel_display_name || f.channelDisplayName || f.channel_name || f.channelName || '';
        const slug = (f.channel_name || f.channelName || '').toLowerCase();
        const isActive = i === active;
        return (
          <div
            key={slug}
            className={`px-3 py-2 cursor-pointer flex items-center gap-3 ${isActive ? 'bg-dark-700' : 'hover:bg-dark-700'}`}
            onClick={() => onSelect(slug)}
            onMouseEnter={() => setActive(i)}
            role="option"
            aria-selected={isActive}
          >
            <span className="text-yellow-400">⭐</span>
            <div className="text-sm text-white truncate">{name} <span className="text-xs text-gray-400">@{slug}</span></div>
          </div>
        );
      })}
    </div>
  );
}
